import { FaMapMarkedAlt, FaGlobeAsia } from 'react-icons/fa';
import heroImage from '../../../assets/images/service-courier-wide.webp';
import { BRANCH_CITIES, CANNOT_SHIP } from './shared';
import type { ServiceContent } from './types';

const courier: ServiceContent = {
  slug: 'courier',
  breadcrumbName: 'Courier Services',
  meta: {
    title: 'Courier Services in Saudi Arabia — Domestic & International | Sahari Cargo',
    description:
      'Send parcels and documents across Saudi Arabia or abroad. Pickup from your address, 12 branches across KSA, and tracking on every shipment. Get a quote.',
  },
  heroImage,
  heroAlt: 'Courier parcels ready for delivery',
  h1: 'Courier Services — Across Saudi Arabia and Abroad',
  intro: [
    "Whether it's a contract going from Jeddah to Riyadh or a parcel on its way to family in India, a courier job comes down to the same three things: collect it on time, keep it safe, and get it into the right hands with tracking you can actually check. Sahari Cargo runs courier services both inside Saudi Arabia and internationally from the Kingdom — through our own branches, with one booking and one tracking number per shipment.",
  ],
  sections: [
    {
      heading: 'Domestic vs International Courier',
      cards: [
        {
          icon: FaMapMarkedAlt,
          title: 'Domestic Courier Within Saudi Arabia',
          text: 'Parcels and documents moving between cities in the Kingdom. Book a pickup from your home, office, or shop, or drop it at your nearest Sahari Cargo branch, and we deliver it to the recipient in the destination city.',
        },
        {
          icon: FaGlobeAsia,
          title: 'International Courier From Saudi Arabia',
          text: 'Parcels, documents, and gifts going abroad. We handle the export paperwork in Saudi Arabia and the clearance at the other end, so the recipient simply receives their shipment.',
          link: { to: '/services/door-to-door', label: 'See door-to-door delivery' },
        },
      ],
    },
    {
      heading: 'Domestic Courier Across Our 12 Branches',
      paragraphs: [
        "We have 12 branches across the Kingdom, from Duba in the north to Jizan in the south. Each branch can accept, collect, and hand over shipments, so a parcel booked in one city is tracked the whole way to the next — not passed between different companies along the route.",
      ],
      chips: BRANCH_CITIES,
    },
    {
      heading: 'International Courier Destinations',
      paragraphs: [
        "From Saudi Arabia we send courier shipments to India, the Philippines, Bangladesh, Pakistan, Nepal, Sri Lanka, Malaysia, Singapore, Brunei, and the United Kingdom. If your destination isn't on this list, contact us — we can usually confirm the same day whether we can reach it.",
      ],
      countries: true,
    },
    {
      heading: 'How Our Courier Service Works',
      steps: [
        { title: 'Book your shipment', text: 'Book online or at any branch with the sender, recipient, and parcel details.' },
        { title: 'Pickup or drop-off', text: 'We collect from your address, or you drop it at your nearest branch.' },
        { title: 'Labelled and recorded', text: 'Every box gets its own number, so each one can be traced individually.' },
        { title: 'On the move', text: 'Your shipment travels to the destination city or country, with tracking updated at each stage.' },
        { title: 'Delivered', text: 'The recipient receives the shipment and you can see it marked delivered in tracking.' },
      ],
    },
    {
      heading: 'What You Can Send',
      paragraphs: [
        'Documents, personal belongings, clothing, gifts for family, small household items, and business parcels such as samples or stock. For larger or heavier shipments, our door-to-door cargo service by air or sea is usually the better fit.',
      ],
      warning: CANNOT_SHIP,
    },
    {
      heading: 'Why Choose Sahari Cargo for Courier',
      paragraphs: [
        "Because your parcel stays with one company from the moment we collect it. Our own staff handle it at every branch, every box is numbered and tracked, and if you need to know where something is, you call a team who can look it up — not a call centre reading the same status you can already see.",
      ],
    },
  ],
  faqs: [
    {
      q: 'Do you deliver within Saudi Arabia?',
      a: 'Yes. We run domestic courier between the cities where we have branches — Jeddah, Makkah, Madinah, Riyadh, Taif, Yanbu, Rabigh, Abha, Khamis Mushait, Jizan, Muhayil, and Duba. Contact us for other locations in the Kingdom.',
    },
    {
      q: 'Which countries can I send a parcel to?',
      a: "India, the Philippines, Bangladesh, Pakistan, Nepal, Sri Lanka, Malaysia, Singapore, Brunei, and the United Kingdom. If your destination isn't listed, get in touch — we can often confirm the same day whether we can serve it.",
    },
    {
      q: 'Can you pick up the parcel from my home or office?',
      a: 'Yes. Book a pickup and we collect from your home, office, or shop. You can also drop the parcel at your nearest Sahari Cargo branch.',
    },
    {
      q: 'How do I track my parcel?',
      a: "Every shipment gets a tracking number when it's booked. Enter it on our Tracking page to see each stage, from pickup through to delivery.",
    },
    {
      q: "What items can't I send by courier?",
      a: 'Illegal drugs, explosives, weapons, hazardous chemicals, and certain live animals cannot be shipped. All items must be declared correctly when you book.',
    },
  ],
  cta: {
    title: 'Need something sent?',
    text: "Tell us where it's going — within Saudi Arabia or abroad — and we'll give you a quote.",
  },
};

export default courier;
